import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle, MapPin, Phone, Mail, MessageCircle, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const OTP_API_URL = "https://demo.lolcdn.xyz";

const contactMethods = [
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Email', value: 'email' },
  { label: 'Phone Call', value: 'phone' }
];

export const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsappNumber: '',
    contactMethod: '',
    message: ''
  });
  
  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Reset OTP state if email changes
    if (name === 'email') {
      setOtpSent(false);
      setEmailVerified(false);
      setEmailOtp('');
      setOtpCountdown(0);
    }
  };

  const validateWhatsApp = (number) => {
    if (!number) return true;
    const normalized = number.replace(/[\s()-]/g, '');
    return /^\+\d{5,15}$/.test(normalized);
  };

  const sendOtp = async () => {
    if (!formData.email) {
      toast({
        variant: "destructive",
        title: "Warning",
        description: "Please enter an email first."
      });
      return;
    }

    // Validate WhatsApp number if provided
    if (formData.whatsappNumber) {
      const raw = String(formData.whatsappNumber).trim();
      const normalized = raw.replace(/[\s()-]/g, '');
      if (!/^\+\d{5,15}$/.test(normalized)) {
        toast({
          variant: "destructive",
          title: "Invalid WhatsApp Number",
          description: "Please provide a valid WhatsApp number with international ISD code (e.g. +1234567890)."
        });
        return;
      }
      setFormData(prev => ({ ...prev, whatsappNumber: normalized }));
    } else if (formData.contactMethod === 'whatsapp') {
      toast({
        variant: "destructive",
        title: "Missing WhatsApp Number",
        description: "Please enter your WhatsApp number with ISD code (e.g. +1234567890)."
      });
      return;
    }

    setOtpLoading(true);

    try {
      const payload = {
        name: formData.name || 'Guest',
        email: formData.email,
        phone: formData.whatsappNumber || '',
        message: formData.message || ''
      };

      const res = await axios.post(`${OTP_API_URL}/send-otp`, payload);
      setOtpSent(true);
      setEmailVerified(false);
      setEmailOtp('');
      setOtpCountdown(60);

      toast({
        title: "OTP Sent",
        description: res.data?.message || "OTP sent to your email. Please check your inbox."
      });
    } catch (err) {
      let detail = 'Failed to send OTP.';
      if (err.response?.data?.error) {
        detail = err.response.data.error;
      }
      toast({
        variant: "destructive",
        title: "Error",
        description: detail
      });

      if (err.response && [500, 503].includes(err.response.status)) {
        setOtpSent(false);
        setOtpCountdown(0);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!formData.email) {
      toast({
        variant: "destructive",
        title: "Warning",
        description: "Email is required for verification."
      });
      return;
    }
    if (!emailOtp) {
      toast({
        variant: "destructive",
        title: "Warning",
        description: "Please enter the OTP."
      });
      return;
    }

    setVerifyLoading(true);
    try {
      await axios.post(`${OTP_API_URL}/verify-otp`, {
        email: formData.email,
        otp: emailOtp
      });

      setEmailVerified(true);
      setOtpSent(false);
      setEmailOtp('');
      setOtpCountdown(0);

      toast({
        title: "Message Sent",
        description: `Your message has been sent successfully. We will contact you within 24 hours on your preferred contact method: ${formData.contactMethod || 'Email'}.`
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        whatsappNumber: '',
        contactMethod: '',
        message: ''
      });
    } catch (err) {
      let detail = 'Failed to verify OTP.';
      if (err.response?.data?.error) {
        detail = err.response.data.error;
      }

      if (err.response && [410, 429].includes(err.response.status)) {
        setOtpSent(false);
        setOtpCountdown(0);
        setEmailOtp('');
      }

      toast({
        variant: "destructive",
        title: "Verification Error",
        description: detail
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const canSendOtp = formData.name && formData.email && formData.contactMethod && formData.message && otpCountdown === 0;

  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left Content - Contact Info */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2 bg-[#003333] rounded-2xl p-8 md:p-10 text-white"
          >
            <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
            
            <div className="space-y-8">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Our Address</h4>
                  <p className="text-white/70 text-sm leading-relaxed">
                    71-75 Shelton Street, Covent Garden<br />
                    London, United Kingdom<br />
                    WC2H 9JQ
                  </p>
                </div>
              </div>
              
              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Call Us</h4>
                  <a 
                    href="tel:+442035763417" 
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    +44 20 3576 3417
                  </a>
                </div>
              </div>
              
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Email Us</h4>
                  <a 
                    href="mailto:hello@lolcorp.co.uk" 
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    hello@lolcorp.co.uk
                  </a>
                </div>
              </div>
              
              {/* WhatsApp */}
              <div className="pt-4">
                <a
                  href="https://wa.link/8qxh5o"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-full px-6 py-3 text-sm font-medium transition-all"
                  data-testid="whatsapp-link"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat with us on WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
          
          {/* Right Content - Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
              Get in Touch
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#003333] mb-6">
              Let's Discuss Your Project
            </h2>
            <p className="text-slate-600 mb-8">
              Ready to transform your internal systems? Share your project details and we'll 
              get back to you within 24 hours.
            </p>
            
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6" data-testid="contact-form">
              {/* Name and Email Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#003333] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={otpSent}
                    className="w-full px-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all disabled:opacity-60"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#003333] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={otpSent}
                    className="w-full px-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all disabled:opacity-60"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              
              {/* WhatsApp Number */}
              <div>
                <label htmlFor="whatsappNumber" className="block text-sm font-medium text-[#003333] mb-2">
                  WhatsApp Number (with country code)
                </label>
                <input
                  type="tel"
                  id="whatsappNumber"
                  name="whatsappNumber"
                  placeholder="+44 20 1234 5678"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  disabled={otpSent}
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all disabled:opacity-60"
                  data-testid="contact-whatsapp-input"
                />
              </div>
              
              {/* Preferred Contact Method */}
              <div>
                <label htmlFor="contactMethod" className="block text-sm font-medium text-[#003333] mb-2">
                  Preferred Contact Method *
                </label>
                <select
                  id="contactMethod"
                  name="contactMethod"
                  value={formData.contactMethod}
                  onChange={handleChange}
                  required
                  disabled={otpSent}
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-lg text-[#003333] focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all appearance-none cursor-pointer disabled:opacity-60"
                  data-testid="contact-method-select"
                >
                  <option value="" disabled>Select a contact method</option>
                  {contactMethods.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#003333] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your project and requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  required
                  disabled={otpSent}
                  className="w-full px-4 py-3 bg-[#F8FAFC] border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all resize-none disabled:opacity-60"
                  data-testid="contact-message-input"
                />
              </div>

              {/* OTP Section */}
              {!otpSent && !emailVerified && (
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={!canSendOtp || otpLoading}
                    className="group inline-flex items-center justify-center gap-3 bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    data-testid="send-otp-button"
                  >
                    {otpLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-5 h-5" />
                        Verify & Send Message
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* OTP Input */}
              {otpSent && !emailVerified && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4 p-6 bg-[#F8FAFC] rounded-xl border border-[#003333]/10"
                >
                  <div className="flex items-center gap-2 text-[#003333]">
                    <Mail className="w-5 h-5" />
                    <p className="font-medium">Enter the OTP sent to {formData.email}</p>
                  </div>
                  
                  <div className="flex gap-4">
                    <input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#003333] text-center text-xl tracking-widest font-mono placeholder:text-slate-400 placeholder:text-base placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333]"
                      data-testid="otp-input"
                    />
                    
                    <button
                      type="button"
                      onClick={verifyOtp}
                      disabled={emailOtp.length !== 6 || verifyLoading}
                      className="inline-flex items-center justify-center gap-2 bg-[#003333] text-white hover:bg-[#004d4d] rounded-lg px-6 py-3 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="verify-otp-button"
                    >
                      {verifyLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Verify
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <p className="text-slate-500">
                      Didn't receive the code?
                    </p>
                    {otpCountdown > 0 ? (
                      <span className="text-slate-400">
                        Resend in {otpCountdown}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={sendOtp}
                        disabled={otpLoading}
                        className="text-[#003333] font-medium hover:underline disabled:opacity-50"
                        data-testid="resend-otp-button"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Success State */}
              {emailVerified && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 p-4 bg-green-50 text-green-800 rounded-lg"
                  data-testid="contact-success-message"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Your message has been sent successfully. We will contact you within 24 hours.</span>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
